import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext.ts';

import { Comments } from '../Comments/index.ts';
import type { FeedEventComment as SummaryLineCommentUpdate } from '../../feedCardTypes.ts';
import type { RenderedContentOptions } from '../RenderedContent.tsx';
import { RenderedContent } from '../RenderedContent.tsx';
import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../Feed.tsx';

import { FeedCardTitle } from './FeedCardTitle.tsx';
import { FeedCardByLine } from './FeedCardByLine.tsx';
import { FeedCardActionCounts, ShowCommentState } from './FeedCardActionCounts.tsx';
import { FeedCardActions } from './FeedCardActions/index.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FollowingCardBody = styled.div({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	paddingTop: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopularCardBody = styled.div({
	flex: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentExcerpt = styled.div({
	maxHeight: '40px',
	marginBottom: token('space.150'),
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageDetails = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndActionsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	marginBottom: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndBylineWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageDetailsContentWrapper = styled.div({
	minWidth: 0,
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentsWrapper = styled.div({
	marginTop: token('space.150'),
	paddingTop: token('space.150'),
	paddingBottom: 0,
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
});

const getShowCommentState = (
	shouldHideComments?: boolean,
	comment?: SummaryLineCommentUpdate,
	commentVisible?: boolean,
): ShowCommentState => {
	if (!shouldHideComments) {
		return ShowCommentState.Number;
	}
	if (!!comment) {
		return commentVisible ? ShowCommentState.HideComment : ShowCommentState.ShowComment;
	}
	return ShowCommentState.NoShow;
};

type FeedCardBodyProps = {
	contentId: string;
	contentTitle: string;
	contentType: string;
	contentSubType?: string | null;
	contentUrl: string;
	spaceId?: string;
	spaceName?: string;
	spaceUrl?: string;
	authorName?: string;
	authorId?: string;
	ownerName?: string;
	ownerId?: string;
	emoji?: string;
	customIconUrl?: string;
	contentExcerpt?: string;

	numComments: number;
	hideSocialProof?: boolean;

	comment?: SummaryLineCommentUpdate;
	renderedContent?: RenderedContentOptions;
	popularCardLayout?: boolean;
	timestamp?: string;
	hideActions?: boolean;

	source: string;
	shouldHideComments?: boolean;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
	hasAiSnippet?: boolean;
};

export const FeedCardBody: FC<FeedCardBodyProps> = ({
	contentId,
	contentTitle,
	contentType,
	contentSubType,
	contentUrl,
	spaceId,
	spaceName,
	spaceUrl,
	authorName,
	authorId,
	ownerName,
	ownerId,
	emoji,
	customIconUrl,
	contentExcerpt,
	numComments,
	hideSocialProof,
	comment,
	renderedContent,
	popularCardLayout = false,
	timestamp,
	hideActions,
	source,
	shouldHideComments,
	popularFeedTimeGranularity,
	followingFeedSortby,
	hasAiSnippet,
}) => {
	const CardBody = popularCardLayout ? PopularCardBody : FollowingCardBody;
	// check if page owner exists and content is not a blog
	// checking against the name here for the case of anonymous users
	const shouldUsePageOwner = ownerName && contentType === 'page';

	const [commentVisible, setCommentVisible] = useState(false);

	const onCommentToggleClick = useCallback(() => {
		setCommentVisible(!commentVisible);
	}, [commentVisible]);

	const showCommentState: ShowCommentState = getShowCommentState(
		shouldHideComments,
		comment,
		commentVisible,
	);

	return (
		<ReactionsProvider contentId={contentId}>
			<CardBody>
				<PageDetails>
					<PageDetailsContentWrapper>
						<TitleAndActionsWrapper>
							<TitleAndBylineWrapper>
								<FeedCardTitle
									contentTitle={contentTitle}
									contentUrl={contentUrl}
									contentType={contentType}
									contentSubType={contentSubType}
									emoji={emoji}
									customIconUrl={customIconUrl}
									popularFeedTimeGranularity={popularFeedTimeGranularity}
									followingFeedSortby={followingFeedSortby}
									hasAiSnippet={hasAiSnippet}
								/>
								{shouldUsePageOwner ? (
									<FeedCardByLine
										spaceName={spaceName}
										spaceUrl={spaceUrl}
										ownerName={ownerName}
										ownerId={ownerId}
										timestamp={timestamp}
										popularFeedTimeGranularity={popularFeedTimeGranularity}
										followingFeedSortby={followingFeedSortby}
									/>
								) : (
									<FeedCardByLine
										spaceName={spaceName}
										spaceUrl={spaceUrl}
										authorName={authorName}
										authorId={authorId}
										timestamp={timestamp}
										popularFeedTimeGranularity={popularFeedTimeGranularity}
										followingFeedSortby={followingFeedSortby}
									/>
								)}
							</TitleAndBylineWrapper>
							{!hideActions && <FeedCardActions contentId={contentId} />}
						</TitleAndActionsWrapper>

						{contentExcerpt && <ContentExcerpt>{contentExcerpt}</ContentExcerpt>}
						{!hideSocialProof && (
							<FeedCardActionCounts
								numComments={numComments}
								contentId={contentId}
								contentType={contentType}
								spaceId={spaceId}
								source={source}
								showCommentState={showCommentState}
								onCommentToggleClick={onCommentToggleClick}
								popularFeedTimeGranularity={popularFeedTimeGranularity}
								followingFeedSortby={followingFeedSortby}
							/>
						)}
					</PageDetailsContentWrapper>
				</PageDetails>

				{comment && (!shouldHideComments || commentVisible) && (
					<CommentsWrapper>
						<Comments comments={[comment]} pageId={contentId} contentType={contentType} />
					</CommentsWrapper>
				)}

				{renderedContent && <RenderedContent content={renderedContent} />}
			</CardBody>
		</ReactionsProvider>
	);
};
